import { type FC, useEffect, useState } from "react";

import type { IssueWorkspaceLaunchResponse } from "./types";

import { Button } from "../../common/Button";
import { wakeBlockPresentation } from "../blackboardModels";
import { WakeBlockCallout } from "./WakeBlockCallout";

type StageTone = "done" | "active" | "failed" | "muted" | "warn";

function toneFor(state: string): StageTone {
  switch (state) {
    case "ready":
    case "queued": {
      return "done";
    }
    case "pending":
    case "provisioning": {
      return "active";
    }
    case "failed": {
      return "failed";
    }
    case "partial":
    case "wake_blocked": {
      return "warn";
    }
    default: {
      return "muted";
    }
  }
}

const TONE_GLYPH: Record<StageTone, string> = {
  active: "▸",
  done: "■",
  failed: "✕",
  muted: "·",
  warn: "!",
};

const TONE_COLOR: Record<StageTone, string> = {
  active: "text-sky-600 dark:text-sky-400",
  done: "text-emerald-600 dark:text-emerald-400",
  failed: "text-rose-600 dark:text-rose-400",
  muted: "text-slate-600 dark:text-slate-500",
  warn: "text-amber-600 dark:text-amber-400",
};

const StageRow: FC<{
  label: string;
  state: string;
  detail?: string | null;
}> = ({ label, state, detail }) => {
  const tone = toneFor(state);
  return (
    <div className="flex items-start gap-2 py-1">
      <span className={`font-mono text-[11px] ${TONE_COLOR[tone]}`}>
        {TONE_GLYPH[tone]}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-slate-700 dark:text-slate-200">
            {label}
          </span>
          <span
            className={`shrink-0 font-mono text-[10px] uppercase tracking-wider ${TONE_COLOR[tone]}`}
          >
            {state.replace(/_/gu, " ")}
          </span>
        </div>
        {detail && (
          <div className="mt-0.5 truncate font-mono text-[10px] text-slate-500 dark:text-slate-400">
            {detail}
          </div>
        )}
      </div>
    </div>
  );
};

/** Is the reported wake block still in force at `nowMs`?
 *
 * The server marks wake-block snapshots terminal, so an open dialog can hold a
 * snapshot long past the block's expiry. An absent or unparseable
 * `wakeBlockedUntil` is treated as active: suppressing a retry that would fail
 * anyway is cheaper than promising one that cannot run. */
export function isWakeBlockWindowActive(
  wakeBlockedUntil: string | null,
  nowMs: number,
): boolean {
  if (wakeBlockedUntil === null) {
    return true;
  }
  const expiryMs = Date.parse(wakeBlockedUntil);
  if (!Number.isFinite(expiryMs)) {
    return true;
  }
  return expiryMs > nowMs;
}

/** Delay until the block lapses, or null when no timer is warranted (no
 * expiry, unparseable expiry, or already lapsed). */
export function wakeBlockExpiryDelayMs(
  wakeBlockedUntil: string | null,
  nowMs: number,
): number | null {
  if (wakeBlockedUntil === null) {
    return null;
  }
  const expiryMs = Date.parse(wakeBlockedUntil);
  if (!Number.isFinite(expiryMs) || expiryMs <= nowMs) {
    return null;
  }
  return expiryMs - nowMs;
}

interface ProgressViewProps {
  snapshot: IssueWorkspaceLaunchResponse;
  polling: boolean;
  onOpenChannel: () => void;
  onRetry: () => void;
  retrying: boolean;
  /** Gates the provider-configuration CTA; the server enforces the real
   * boundary, so the least-privileged view is the safe default. */
  canManageOrgSettings?: boolean;
}

/** Launch progress — driven by re-POST snapshots of the operation. Named
 * stages with per-stage status; the channel is the recovery handle, so
 * "Open channel" is always primary once it exists. */
export const ProgressView: FC<ProgressViewProps> = ({
  snapshot,
  polling,
  onOpenChannel,
  onRetry,
  retrying,
  canManageOrgSettings = false,
}) => {
  const { stages } = snapshot;
  const settled = snapshot.state !== "in_progress";
  const { wakeBlockedUntil } = stages.openingContext;
  const reportedWakeBlock = stages.openingContext.state === "wake_blocked";
  // Re-POSTing the launch cannot clear an active wake block — the persistence
  // gate returns wake_blocked again — so the generic retry stays hidden while
  // the block is live. It remains available for genuinely retryable failures.
  // The snapshot is terminal and never re-polled, so "live" has to be judged
  // against the clock here, with a timer to re-render at the expiry instant.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const wakeBlocked =
    reportedWakeBlock && isWakeBlockWindowActive(wakeBlockedUntil, nowMs);

  useEffect(() => {
    if (!reportedWakeBlock) {
      return;
    }
    const delayMs = wakeBlockExpiryDelayMs(wakeBlockedUntil, Date.now());
    if (delayMs === null) {
      return;
    }
    const timer = setTimeout(() => {
      setNowMs(Date.now());
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [reportedWakeBlock, wakeBlockedUntil]);
  const hasFailure =
    snapshot.state === "partial" ||
    snapshot.errors.some((error) => error.retryable);

  const actorSummary =
    stages.actors.state === "skipped"
      ? null
      : `${stages.actors.items.filter((item) => item.state === "ready").length}/${stages.actors.items.length} spawned`;

  const repoSummary =
    stages.repositories.state === "skipped" ||
    stages.repositories.requested === 0
      ? null
      : `${stages.repositories.ready}/${stages.repositories.requested} ready`;

  const contextDetail = wakeBlocked
    ? `Lead will receive the ticket when the block clears (${
        wakeBlockPresentation(stages.openingContext.wakeBlockedReason)
          .reasonLabel
      })`
    : (stages.openingContext.error ??
      (stages.openingContext.role === "fallback"
        ? "Delivered to a fallback lead"
        : null));

  return (
    <div className="space-y-4">
      <div className="border border-slate-200 dark:border-slate-800 px-3 py-2">
        <div className="font-mono text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
          #{snapshot.channel.displayName}
        </div>
        <div className="truncate font-mono text-[10px] text-slate-500 dark:text-slate-400">
          {snapshot.issue.issueKey} · {snapshot.issue.summary}
        </div>
      </div>

      <div className="divide-y divide-slate-200 dark:divide-slate-800/60 border border-slate-200 dark:border-slate-800 px-3 py-1">
        <StageRow label="Channel" state={stages.channel.state} />
        <StageRow label="Ticket context" state={stages.ticketMessage.state} />
        {stages.environment.state !== "skipped" && (
          <StageRow
            label="Sandbox"
            state={stages.environment.state}
            detail={stages.environment.error}
          />
        )}
        {stages.repositories.state !== "skipped" &&
          stages.repositories.requested > 0 && (
            <StageRow
              label="Repositories"
              state={stages.repositories.state}
              detail={
                stages.repositories.failed
                  .map((failure) => `${failure.repositoryId}: ${failure.error}`)
                  .join("; ") || repoSummary
              }
            />
          )}
        <StageRow
          label="Agents"
          state={stages.actors.state}
          detail={
            stages.actors.items
              .filter((item) => item.error)
              .map((item) => `${item.displayName}: ${item.error ?? ""}`)
              .join("; ") || actorSummary
          }
        />
        <StageRow
          label="Lead wake"
          state={stages.openingContext.state}
          detail={contextDetail}
        />
      </div>

      {wakeBlocked && (
        <WakeBlockCallout
          canManageOrgSettings={canManageOrgSettings}
          reason={stages.openingContext.wakeBlockedReason}
          wakeBlockedUntil={stages.openingContext.wakeBlockedUntil}
        />
      )}

      {polling && !settled && (
        <p className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
          Provisioning. This can take a few minutes with a sandbox attached.
          Closing this dialog will not cancel the launch.
        </p>
      )}

      <div className="flex items-center justify-end gap-2">
        {settled && hasFailure && !wakeBlocked && (
          <Button
            size="sm"
            variant="secondary"
            onClick={onRetry}
            disabled={retrying}
          >
            {retrying ? "Retrying…" : "Retry failed steps"}
          </Button>
        )}
        <Button size="sm" onClick={onOpenChannel}>
          Open channel
        </Button>
      </div>
    </div>
  );
};
